import { useCallback, useMemo } from 'react';

import { ICON_VOLATILITY_HIGH, ICON_VOLATILITY_LOW, ICON_VOLATILITY_MID } from '~/components/icon/index';
import currencyFormatter from '~/libs/currency-formatter';
import { selectCurrency, selectLocale } from '~/components/account/ducks/index';
import useTranslations from '~/components/translation/use-translations';
import { isNumber } from '~/components/payment/deposit/data/string-num-clean';
import { useAppSelector } from '~/store/types';

import { EnhancedGame } from '../types';

import About from './about-section';
import { CombinedGameInfo } from './types';
import { StyledIcon } from './game-info.styles';
import Features from './features-section';
import PromotionSection from './promotion-section';
import {
	InfoContainer,
	InfoRow,
	RowTitle,
	RowValue,
	VolatilityValueWrapper,
	JackpotContainer,
	JackpotAmount,
	JackpotWord,
	SecondarySection as SecondarySectionStyled
} from './secondary-section.styles';

const TEXT_KEYS = [
	'VOLATILITY_LOW',
	'VOLATILITY_MEDIUM',
	'VOLATILITY_LOW_MEDIUM',
	'VOLATILITY_MEDIUM_HIGH',
	'VOLATILITY_HIGH',
	'VOLATILITY',
	'RETURN_TO_PLAYER',
	'PAYLINES',
	'BETTING_RANGE',
	'MAXIMUM_WIN',
	'PROGRESSIVE_JACKPOT',
	'JACKPOT',
	'ABOUT',
	'MIN_BET_VALUE',
	'GAME_INFO_DEALER',
	'GAME_INFO_FEATURES',
	'GAME_INFO_ABOUT_MORE',
	'GAME_INFO_ABOUT_LESS',
	'GAME_INFO_SIMILAR_GAMES_TITLE',
	'GAME_INFO_SIMILAR_GAMES_DESCRIPTION',
	'GAME_INFO_PROVIDER_GAMES_TITLE',
	'GAME_INFO_PROVIDER_GAMES_DESCRIPTION'
];

const includesDecimal = (number: number) => {
	return number % 1 !== 0;
};

type Props = {
	categoryCode: string;
	gameInfo: CombinedGameInfo;
	game: EnhancedGame;
};

const SecondarySection = ({ categoryCode, gameInfo, game }: Props) => {
	const currency = useAppSelector(selectCurrency);
	const locale = useAppSelector(selectLocale);
	const isLiveCasinoGame = categoryCode === 'CASINO_LIVE_CASINO';
	const { translations: t, isLoadingTranslations } = useTranslations([
		...TEXT_KEYS,
		...(gameInfo?.textKey ? [gameInfo?.textKey] : [])
	]);

	const volatilityIcon = useMemo(() => {
		switch (gameInfo?.volatility?.toLowerCase()) {
			case 'low':
				return <StyledIcon type={ICON_VOLATILITY_LOW} color="var(--game-info-volatility-icon-color)" />;
			case 'medium':
			case 'low-medium':
			case 'medium-high':
				return <StyledIcon type={ICON_VOLATILITY_MID} color="var(--game-info-volatility-icon-color)" />;

			case 'high':
				return <StyledIcon type={ICON_VOLATILITY_HIGH} color="var(--game-info-volatility-icon-color)" />;

			default:
				return null;
		}
	}, [gameInfo]);

	const volatilityText = useMemo(() => {
		if (gameInfo?.volatility) {
			switch (gameInfo?.volatility?.toLowerCase()) {
				case 'low':
					return t.VOLATILITY_LOW;
				case 'medium':
					return t.VOLATILITY_MEDIUM;
				case 'low-medium':
					return t.VOLATILITY_LOW_MEDIUM;
				case 'medium-high':
					return t.VOLATILITY_MEDIUM_HIGH;
				case 'high':
					return t.VOLATILITY_HIGH;
				default:
					return '';
			}
		}
		return null;
	}, [
		gameInfo,
		t.VOLATILITY_LOW,
		t.VOLATILITY_MEDIUM,
		t.VOLATILITY_HIGH,
		t.VOLATILITY_LOW_MEDIUM,
		t.VOLATILITY_MEDIUM_HIGH
	]);

	const formatNumber = useCallback(
		(number: number, showDecimals?: boolean) => {
			let finalShowDecimals = showDecimals;
			if (typeof showDecimals === 'undefined') {
				finalShowDecimals = includesDecimal(number);
			}
			return currencyFormatter.format(number, currency, locale, !finalShowDecimals) as string;
		},
		[currency, locale]
	);

	const maximumWin = useMemo(() => {
		if (!gameInfo.multiplierMaxWin && !gameInfo.amountMaxWin) {
			return null;
		}

		if (isLiveCasinoGame && gameInfo.amountMaxWin) {
			return formatNumber(gameInfo.amountMaxWin);
		}

		return `${gameInfo.multiplierMaxWin || ''}X`;
	}, [formatNumber, gameInfo.amountMaxWin, gameInfo.multiplierMaxWin, isLiveCasinoGame]);

	const bettingRange = useMemo(() => {
		if (isLiveCasinoGame) {
			if (!gameInfo?.betLimits?.max) return null;

			const showDecimals =
				includesDecimal(gameInfo.betLimits.min || 0) || includesDecimal(gameInfo.betLimits.max);

			return `${formatNumber(gameInfo.betLimits.min || 0, showDecimals)} - ${formatNumber(
				gameInfo.betLimits.max,
				showDecimals
			)}`;
		}
		if (!gameInfo.maxBetRange) return null;

		const showDecimals = includesDecimal(gameInfo.minBetRange || 0) || includesDecimal(gameInfo.maxBetRange);

		return `${formatNumber(gameInfo.minBetRange || 0, showDecimals)} - ${formatNumber(
			gameInfo.maxBetRange,
			showDecimals
		)}`;
	}, [
		formatNumber,
		gameInfo?.betLimits?.max,
		gameInfo?.betLimits?.min,
		gameInfo?.maxBetRange,
		gameInfo?.minBetRange,
		isLiveCasinoGame
	]);

	if (isLoadingTranslations) {
		return null;
	}

	return (
		<SecondarySectionStyled>
			<div>
				{!!gameInfo.jackpot && (
					<JackpotContainer>
						<JackpotAmount>{gameInfo.jackpot}</JackpotAmount>
						<JackpotWord>{t.JACKPOT}</JackpotWord>
					</JackpotContainer>
				)}

				<InfoContainer>
					{!!gameInfo.volatility && (
						<InfoRow>
							<RowTitle>{t.VOLATILITY}</RowTitle>
							<VolatilityValueWrapper>
								{volatilityIcon}
								<RowValue>{volatilityText}</RowValue>
							</VolatilityValueWrapper>
						</InfoRow>
					)}
					{!!gameInfo.rtp && (
						<InfoRow>
							<RowTitle>{t.RETURN_TO_PLAYER}</RowTitle>
							<RowValue>{gameInfo.rtp} %</RowValue>
						</InfoRow>
					)}
					{!!gameInfo.payLines && (
						<InfoRow>
							<RowTitle>{t.PAYLINES}</RowTitle>
							<RowValue>{gameInfo.payLines}</RowValue>
						</InfoRow>
					)}
					{bettingRange && (
						<InfoRow>
							<RowTitle>{t.BETTING_RANGE}</RowTitle>
							<RowValue>{bettingRange}</RowValue>
						</InfoRow>
					)}
					{!!gameInfo.dealer && (
						<InfoRow>
							<RowTitle>{t.GAME_INFO_DEALER}</RowTitle>
							<RowValue>{gameInfo.dealer}</RowValue>
						</InfoRow>
					)}
					{!bettingRange && isLiveCasinoGame && isNumber(gameInfo?.betLimits?.min) && (
						<InfoRow>
							<RowTitle>{t.MIN_BET_VALUE}</RowTitle>
							<RowValue>{formatNumber(gameInfo?.betLimits?.min)}</RowValue>
						</InfoRow>
					)}
					{!bettingRange && !isLiveCasinoGame && isNumber(gameInfo.minBetRange) && (
						<InfoRow>
							<RowTitle>{t.MIN_BET_VALUE}</RowTitle>
							<RowValue>{formatNumber(gameInfo.minBetRange)}</RowValue>
						</InfoRow>
					)}
					{!!maximumWin && (
						<InfoRow>
							<RowTitle>{t.MAXIMUM_WIN}</RowTitle>
							<RowValue>{maximumWin}</RowValue>
						</InfoRow>
					)}
				</InfoContainer>

				{!!gameInfo?.features?.length && <Features features={gameInfo.features} title={t.GAME_INFO_FEATURES} />}

				{!!gameInfo.textKey && game?.name && (
					<About
						gameName={game.name}
						title={t.ABOUT}
						text={t[gameInfo.textKey]}
						moreText={t.GAME_INFO_ABOUT_MORE}
						lessText={t.GAME_INFO_ABOUT_LESS}
					/>
				)}

				<PromotionSection game={game} t={t} />
			</div>
		</SecondarySectionStyled>
	);
};

export default SecondarySection;
